/**
 * New decision — one screen, three fields.
 *
 * Route: /brief/new
 *
 * Replaces the four-step, sixteen-field wizard that held everything in
 * React state until the last step. The decision exists as a `draft` the
 * moment it has a question; options, what matters, and everyone else are
 * added on the decision page itself, one autosaved field at a time.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Field, TextInput } from '@/components/ds';
import { createBrief, getWorkspaceBriefCount } from '../../services/brief/briefService';
import { useTenantStore } from '../../stores/prioritization/tenantStore';
import { useUserStore } from '../../stores/prioritization/userStore';
import { canCreateBrief, FREE_BRIEF_LIMIT } from '../../lib/entitlements';
import { btnPrimary } from '@/components/mono/primitives';

export default function NewDecisionPage() {
  const navigate = useNavigate();
  const currentWorkspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const workspacePlan = useTenantStore((s) => s.workspacePlan);
  const userName = useUserStore((s) => s.userName);

  // The free plan's limit, asked the way the Decisions list asks it (the same
  // helper, the same count) — so the form is not drawn for a workspace that has
  // used all of its decisions, instead of refusing after the fill-in (§2.8).
  const [activeCount, setActiveCount] = useState<number | null>(null);
  useEffect(() => {
    if (!currentWorkspaceId) return;
    let cancelled = false;
    getWorkspaceBriefCount(currentWorkspaceId)
      .then((count) => {
        if (!cancelled) setActiveCount(count);
      })
      .catch(() => {
        if (!cancelled) setActiveCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, [currentWorkspaceId]);
  const atLimit = !!workspacePlan && activeCount !== null && !canCreateBrief(workspacePlan, activeCount);

  const [title, setTitle] = useState('');
  const [decider, setDecider] = useState(userName && userName !== 'User' ? userName : '');
  const [deadline, setDeadline] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canCreate = title.trim().length > 0 && !saving;

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentWorkspaceId) {
      setError("We couldn't find your workspace. Reload the page and try again.");
      return;
    }
    if (!title.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const brief = await createBrief(currentWorkspaceId, {
        title,
        decision_authority: decider,
        deadline: deadline || null,
      });
      navigate(`/brief/${brief.id}`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the decision.');
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <div className="border-b border-[var(--rule)] bg-[var(--bg-raised)]">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="/brief" className="inline-flex items-center gap-1.5 text-sm text-[var(--text-ter)] hover:text-[var(--text-pri)] transition-colors">
            <ArrowLeftIcon className="w-4 h-4" />
            Decisions
          </Link>
        </div>
      </div>

      {atLimit ? (
        <div className="max-w-2xl mx-auto px-6 py-10">
          <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-[var(--text-ter)] mb-2">New decision</p>
          <p role="status" className="text-base text-[var(--text-sec)]">
            You have used all {FREE_BRIEF_LIMIT} decisions on the free plan. Archive a decided one to make room, or{' '}
            <a href="mailto:hello@priorities.ai?subject=Decision Brief Pro" className="underline">upgrade for unlimited decisions</a>.
          </p>
          <Link to="/brief" className="inline-block mt-6 text-sm text-[var(--text-ter)] hover:text-[var(--text-pri)] transition-colors">
            Back to decisions
          </Link>
        </div>
      ) : (
      <form onSubmit={handleCreate} className="max-w-2xl mx-auto px-6 py-10">
        <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-[var(--text-ter)] mb-2">New decision</p>
        <h1 className="font-heading font-semibold text-2xl text-[var(--text-pri)] mb-2" style={{ color: 'var(--text-pri)' }}>
          What are you deciding?
        </h1>
        <p className="text-base text-[var(--text-sec)] mb-8">
          Three things to start. You can add the options and what matters on the next screen, and nothing is lost if you leave part-way.
        </p>

        <div className="space-y-6">
          <Field label="The question" required hint='Name the decision, not the project: "Which CRM vendor do we pick?"' htmlFor="nd-title">
            <TextInput
              id="nd-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Which CRM vendor do we pick?"
              autoFocus
              className="text-base"
            />
          </Field>

          <Field label="Who makes the call?" hint="The person or group who will record the final decision. Defaults to you." htmlFor="nd-decider">
            <TextInput
              id="nd-decider"
              value={decider}
              onChange={(e) => setDecider(e.target.value)}
              placeholder="e.g. Priya, or the leadership team"
            />
          </Field>

          <Field label="When does it need to be decided by?" hint="Leave it blank if there is no date yet." htmlFor="nd-deadline">
            <TextInput id="nd-deadline" type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
          </Field>
        </div>

        {error && (
          <div role="alert" className="mt-6 border border-[var(--coral)]/30 bg-[var(--coral)]/10 px-4 py-3 text-sm text-[var(--coral)]">
            {error}
          </div>
        )}

        <div className="mt-8 pt-6 border-t border-[var(--rule)] flex items-center justify-between">
          <Link to="/brief" className="text-sm text-[var(--text-ter)] hover:text-[var(--text-pri)] transition-colors">
            Cancel
          </Link>
          <button
            type="submit"
            disabled={!canCreate}
            {...btnPrimary()}
          >
            {saving ? 'Creating…' : 'Create decision →'}
          </button>
        </div>
      </form>
      )}
    </div>
  );
}
